import { useState } from "react";
import { useDispatch } from "react-redux";
import { LuFileText } from "react-icons/lu";
import Modal from "../../../common/Modal";
import Button from "../../../common/Buttons";
import {
  viewInventoryDocument,
  uploadInventoryDocument,
  removeInventoryDocument,
} from "../../../redux/action/inventoryAction/InventoryAction";
import { notify } from "../../../common/Toast";
import DocRow from "./InventoryDocumentRow";

const DOC_LABELS = {
  quotation: "Quotation Document",
  po: "Purchase Order Document",
  msds: "MSDS Document",
  coa: "CoA Document",
};

export default function InventoryDocuments(props) {
  const { id, poDoc, quotationDoc } = props;
  const [msdsDoc, setMsdsDoc] = useState(props.msdsDoc);
  const [coaDoc, setCoaDoc] = useState(props.coaDoc);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const dispatch = useDispatch();

  const openView = async (docSource) => {
    const res = await dispatch(viewInventoryDocument(docSource));
    if (res?.success && res.downloadUrl) {
      window.open(res.downloadUrl, "_blank");
    } else if (!res?.success) {
      notify.error("Failed to fetch document", res?.error);
    }
  };

  const handleUpload = async (file, docCategory) => {
    setBusyKey(docCategory);
    const res = await dispatch(uploadInventoryDocument(id, file, docCategory));
    setBusyKey(null);

    if (!res?.success) {
      notify.error(`Failed to upload ${docCategory} document`, res?.error);
      return;
    }

    notify.success(
      `${docCategory.toUpperCase()} Uploaded Successfully`,
      `Your ${docCategory} document has been uploaded.`,
    );

    if (docCategory === "msds") setMsdsDoc(res.destination);
    if (docCategory === "coa") setCoaDoc(res.destination);
  };

  const handleRemove = async (docCategory) => {
    const docName = docCategory === "msds" ? msdsDoc : coaDoc;
    setBusyKey(docCategory);
    const res = await dispatch(removeInventoryDocument(id, docName, docCategory));
    setBusyKey(null);

    if (!res?.success) {
      notify.error(`Failed to remove ${docCategory} document`, res?.error);
      return;
    }

    notify.success(
      `${docCategory.toUpperCase()} Removed Successfully`,
      `Your ${docCategory} document has been removed.`,
    );

    if (docCategory === "msds") setMsdsDoc(null);
    else setCoaDoc(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="flex items-center gap-1.5 rounded-md bg-cyan-600/10 px-2.5 py-1.5 text-[12px] font-medium text-cyan-700 transition hover:bg-cyan-600/15"
      >
        <LuFileText size={13} />
        View Docs
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Documents">
        <div className="space-y-2.5">
          <DocRow
            label={DOC_LABELS.quotation}
            docName={quotationDoc}
            viewPath={"/quotations/" + quotationDoc}
            onView={openView}
            removable={false}
            uploadable={false}
          />
          <DocRow
            label={DOC_LABELS.po}
            docName={poDoc}
            viewPath={"/purchase-orders/" + poDoc}
            onView={openView}
            removable={false}
            uploadable={false}
          />
          <DocRow
            label={DOC_LABELS.msds}
            docName={msdsDoc}
            viewPath={"/chemical-docs/" + msdsDoc}
            onView={openView}
            onRemove={() => handleRemove("msds")}
            onUpload={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file, "msds");
            }}
            removable
            uploadable
            busy={busyKey === "msds"}
          />
          <DocRow
            label={DOC_LABELS.coa}
            docName={coaDoc}
            viewPath={"/chemical-docs/" + coaDoc}
            onView={openView}
            onRemove={() => handleRemove("coa")}
            onUpload={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file, "coa");
            }}
            removable
            uploadable
            busy={busyKey === "coa"}
          />
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
}